import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useState } from "react"


import { DiffView } from "@/components/DiffView/DiffView.tsx"
import { Gap } from "@/components/Flex.ts"
import * as Tabs from "@/components/Tabs/Tabs.ts"
import { ChangeStats } from "@/routes/tasks/TaskViewScene/ChangeStats.tsx"
import { versionTitle } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

import type { ScrollableModel } from "@/components/Scrollable/ScrollableModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

enum ChangesTabId {
  All = "all",
  Previous = "previous"
}

export const TaskChangesTab = observer((props: {
  model: TaskViewModel
  scrollableModel: ScrollableModel
}) => {
  const [activeTab, setActiveTab] = useState<ChangesTabId>(ChangesTabId.All)
  const [hoveredTab, setHoveredTab] = useState<ChangesTabId | null>(null)

  const historyItems = props.model.historyItems.get()
  const activeItems = historyItems.filter(item => item.isActive)

  const hasMultipleVersions = activeItems.filter(it => it.number > 0).length > 1
  const currentVersionNumber = activeItems.length > 0 ? activeItems[activeItems.length - 1].number : 0

  const changes = props.model.changesModel.files.get()
  const previousChanges = props.model.changesFromPreviousVersionModel.files.get()

  const emptyStateDisplayed =
    activeTab === ChangesTabId.All && changes.length === 0 ||
    activeTab === ChangesTabId.Previous && previousChanges.length === 0

  return (
    <StyledTabsRoot
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as ChangesTabId)}
      stretch={emptyStateDisplayed}
    >
      <Tabs.List>
        {changes.length > 0 && (
          <Tabs.BorderlessTabsTrigger
            value={ChangesTabId.All}
            onMouseEnter={() => setHoveredTab(ChangesTabId.All)}
            onMouseLeave={() => setHoveredTab(null)}
          >
            All changes
            <ChangeStats
              files={changes}
              isActive={activeTab === ChangesTabId.All}
              isHovered={hoveredTab === ChangesTabId.All}
            />
          </Tabs.BorderlessTabsTrigger>
        )}

        {hasMultipleVersions && (
          <Tabs.BorderlessTabsTrigger
            value={ChangesTabId.Previous}
            onMouseEnter={() => setHoveredTab(ChangesTabId.Previous)}
            onMouseLeave={() => setHoveredTab(null)}
          >
            Diff to {versionTitle(currentVersionNumber - 1).toLowerCase()}
            <ChangeStats
              files={previousChanges}
              isActive={activeTab === ChangesTabId.Previous}
              isHovered={hoveredTab === ChangesTabId.Previous}
            />
          </Tabs.BorderlessTabsTrigger>
        )}
      </Tabs.List>

      <Tabs.Content value={ChangesTabId.All}>
        {!emptyStateDisplayed && <Gap height="12px" />}
        <DiffView model={props.model.changesModel} />
      </Tabs.Content>

      {hasMultipleVersions && (
        <Tabs.Content value={ChangesTabId.Previous}>
          {!emptyStateDisplayed && <Gap height="12px" />}
          <DiffView model={props.model.changesFromPreviousVersionModel} />
        </Tabs.Content>
      )}
    </StyledTabsRoot>
  )
})

const StyledTabsRoot = styled(Tabs.Root)<{ stretch: boolean }>`
  height: ${props => props.stretch ? "100%" : "auto"};
`
